import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { receptionsAPI, warehousesAPI } from '@/lib/api';
import { 
  Package, Truck, FileText, Plus, Trash2, Check, 
  Clock, Hash, ChevronDown, ChevronUp, Eye, Warehouse,
  Minus, ShoppingCart, X, Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '@/contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const BodegaReceptionPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('new');
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [supplierItems, setSupplierItems] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [receptionItems, setReceptionItems] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [invoiceConfirmed, setInvoiceConfirmed] = useState(false);
  
  // Para historial
  const [receptions, setReceptions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedReception, setExpandedReception] = useState(null);
  const [detailDialog, setDetailDialog] = useState({ open: false, reception: null });

  const loadSuppliers = useCallback(async () => {
    try {
      const response = await receptionsAPI.getSuppliers();
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    }
  }, []);

  const loadWarehouses = useCallback(async () => {
    try {
      const response = await warehousesAPI.getActive();
      setWarehouses(response.data);
    } catch (error) {
      toast.error('Error al cargar bodegas');
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const response = await receptionsAPI.getHistory({ limit: 50 });
      setReceptions(response.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const loadItemsBySupplier = useCallback(async (supplier) => {
    setLoadingItems(true);
    try {
      const response = await receptionsAPI.getItemsBySupplier(supplier);
      setSupplierItems(response.data);
    } catch (error) {
      toast.error('Error al cargar productos del proveedor');
    } finally {
      setLoadingItems(false);
    }
  }, []);

  useEffect(() => {
    loadSuppliers();
    loadWarehouses();
  }, [loadSuppliers, loadWarehouses]);
  
  useEffect(() => {
    if (user?.warehouse_id && warehouses.length > 0) {
      setSelectedWarehouse(user.warehouse_id);
    } else if (warehouses.length > 0 && !selectedWarehouse) {
      const central = warehouses.find(w => w.code === 'BODEGA-CENTRAL');
      if (central) setSelectedWarehouse(central.id);
    }
  }, [user?.warehouse_id, warehouses, selectedWarehouse]);

  useEffect(() => {
    if (activeTab === 'history') loadHistory();
  }, [activeTab, loadHistory]);

  useEffect(() => {
    if (selectedSupplier) {
      // Mostrar dialog para pedir número de factura cuando se selecciona proveedor
      setShowInvoiceDialog(true);
      setInvoiceConfirmed(false);
      loadItemsBySupplier(selectedSupplier);
      setReceptionItems({});
    } else {
      setSupplierItems([]);
      setInvoiceConfirmed(false);
    }
  }, [selectedSupplier, loadItemsBySupplier]);

  // Actualizar cantidad de un item
  const updateQuantity = (itemId, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue <= 0) {
      const newItems = { ...receptionItems };
      delete newItems[itemId];
      setReceptionItems(newItems);
    } else {
      setReceptionItems(prev => ({
        ...prev,
        [itemId]: numValue
      }));
    }
  };

  // Incrementar/decrementar cantidad
  const adjustQuantity = (itemId, delta) => {
    const current = receptionItems[itemId] || 0;
    const newValue = Math.max(0, current + delta);
    updateQuantity(itemId, newValue);
  };

  // Items filtrados por búsqueda
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return supplierItems;
    const query = searchQuery.toLowerCase();
    return supplierItems.filter(item => 
      item.name.toLowerCase().includes(query) ||
      item.code.toLowerCase().includes(query)
    );
  }, [supplierItems, searchQuery]);

  // Items en el carrito
  const cartItems = useMemo(() => {
    return Object.entries(receptionItems)
      .filter(([_, qty]) => qty > 0)
      .map(([itemId, quantity]) => {
        const item = supplierItems.find(i => i.id === itemId);
        return item ? { ...item, quantity } : null;
      })
      .filter(Boolean);
  }, [receptionItems, supplierItems]);

  const totalItems = cartItems.length;
  const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleSubmit = async () => {
    if (!selectedSupplier || !selectedWarehouse || !invoiceNumber.trim()) {
      toast.error('Completa los datos de la factura primero');
      setShowInvoiceDialog(true);
      return;
    }

    if (cartItems.length === 0) {
      toast.error('Agrega productos a la recepción');
      return;
    }

    setSubmitting(true);
    try {
      const receptionData = {
        supplier: selectedSupplier,
        warehouse_id: selectedWarehouse,
        invoice_number: invoiceNumber.trim(),
        notes: notes.trim() || null,
        items: cartItems.map(item => ({
          item_id: item.id,
          item_code: item.code,
          item_name: item.name,
          quantity: item.quantity,
          unit: item.unit || 'unidad'
        }))
      };

      await receptionsAPI.create(receptionData);
      toast.success(`Recepción registrada: ${totalItems} productos`);
      
      // Reset
      setReceptionItems({});
      setInvoiceNumber('');
      setNotes('');
      setSelectedSupplier('');
      setInvoiceConfirmed(false);
      setCartOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar recepción');
    } finally {
      setSubmitting(false);
    }
  };

  // Confirmar datos de factura y mostrar productos
  const confirmInvoice = () => {
    if (!invoiceNumber.trim()) {
      toast.error('Ingresa el número de factura');
      return;
    }
    setInvoiceConfirmed(true);
    setShowInvoiceDialog(false);
  };

  // Cancelar la selección de proveedor
  const cancelInvoice = () => {
    setSelectedSupplier('');
    setInvoiceNumber('');
    setNotes('');
    setShowInvoiceDialog(false);
    setInvoiceConfirmed(false);
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "dd MMM yyyy, HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-32" data-testid="bodega-reception-page">
      {/* Header fijo */}
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Truck className="w-5 h-5 text-primary" />
              Recepción
            </h1>
            <p className="text-xs text-slate-500">Registra productos de proveedores</p>
          </div>
          
          {/* Contador del carrito */}
          {totalItems > 0 && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCartOpen(true)}
              className="relative"
            >
              <ShoppingCart className="w-4 h-4 mr-1" />
              <span className="font-bold">{totalItems}</span>
              <span className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {totalQuantity}
              </span>
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="px-4 py-2 bg-white border-b">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="new" className="text-sm">
              <Plus className="w-4 h-4 mr-1" />
              Nueva
            </TabsTrigger>
            <TabsTrigger value="history" className="text-sm">
              <Clock className="w-4 h-4 mr-1" />
              Historial
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Tab: Nueva Recepción */}
        <TabsContent value="new" className="mt-0 px-4 py-4 space-y-4">
          {/* Selector de Proveedor */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <Label className="text-sm font-medium mb-2 block">Proveedor</Label>
            <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
              <SelectTrigger className="h-12 text-base" data-testid="supplier-select">
                <SelectValue placeholder="Selecciona proveedor..." />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map(supplier => (
                  <SelectItem key={supplier} value={supplier} className="text-base py-3">
                    {supplier}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Lista de productos - Solo si la factura está confirmada */}
          {selectedSupplier && invoiceConfirmed && (
            <div className="space-y-3">
              {/* Info de la factura */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800">{selectedSupplier}</p>
                  <p className="text-xs text-green-600">Factura: {invoiceNumber}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowInvoiceDialog(true)}
                  className="text-green-700"
                >
                  Editar
                </Button>
              </div>

              {/* Búsqueda */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar producto..."
                  className="pl-10 h-11"
                />
              </div>

              {loadingItems ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No hay productos</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredItems.map(item => {
                    const quantity = receptionItems[item.id] || 0;
                    const hasQuantity = quantity > 0;
                    
                    return (
                      <motion.div
                        key={item.id}
                        layout
                        className={`bg-white rounded-xl border-2 p-4 transition-colors ${
                          hasQuantity ? 'border-green-400 bg-green-50/50' : 'border-slate-200'
                        }`}
                      >
                        {/* Info del producto */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0 pr-2">
                            <p className="font-semibold text-slate-900 truncate">{item.name}</p>
                            <p className="text-xs text-slate-500">{item.code} • Stock: {item.current_stock || 0} {item.unit}</p>
                          </div>
                          {hasQuantity && (
                            <span className="flex-shrink-0 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                              ✓ {quantity}
                            </span>
                          )}
                        </div>

                        {/* Controles de cantidad */}
                        <div className="flex items-center justify-center gap-3">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-12 w-12 rounded-full text-lg font-bold"
                            onClick={() => adjustQuantity(item.id, -1)}
                            disabled={quantity <= 0}
                          >
                            <Minus className="w-5 h-5" />
                          </Button>
                          
                          <Input
                            type="number"
                            inputMode="decimal"
                            value={quantity || ''}
                            onChange={(e) => updateQuantity(item.id, e.target.value)}
                            placeholder="0"
                            className="w-24 h-14 text-center text-2xl font-bold border-2"
                            data-testid={`qty-${item.code}`}
                          />
                          
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-12 w-12 rounded-full text-lg font-bold bg-primary/10 border-primary text-primary hover:bg-primary hover:text-white"
                            onClick={() => adjustQuantity(item.id, 1)}
                          >
                            <Plus className="w-5 h-5" />
                          </Button>
                        </div>

                        {/* Atajos rápidos */}
                        <div className="flex justify-center gap-2 mt-3">
                          {[5, 10, 20, 50].map(val => (
                            <Button
                              key={val}
                              variant="ghost"
                              size="sm"
                              className="h-8 px-3 text-xs bg-slate-100 hover:bg-slate-200"
                              onClick={() => updateQuantity(item.id, val)}
                            >
                              +{val}
                            </Button>
                          ))}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!selectedSupplier && (
            <div className="text-center py-16 text-slate-500">
              <Truck className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">Selecciona un proveedor</p>
              <p className="text-sm">para ver sus productos</p>
            </div>
          )}
        </TabsContent>

        {/* Tab: Historial */}
        <TabsContent value="history" className="mt-0 px-4 py-4 space-y-3">
          {loadingHistory ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : receptions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No hay recepciones registradas</p>
            </div>
          ) : (
            receptions.map(reception => (
              <div 
                key={reception.id}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden"
              >
                <button
                  onClick={() => setExpandedReception(
                    expandedReception === reception.id ? null : reception.id
                  )}
                  className="w-full p-4 text-left"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">{reception.supplier}</p>
                      <p className="text-sm text-slate-500">
                        {reception.invoice_number} • {reception.items?.length || 0} productos
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {formatDate(reception.created_at)}
                      </p>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${
                      expandedReception === reception.id ? 'rotate-180' : ''
                    }`} />
                  </div>
                </button>
                
                <AnimatePresence>
                  {expandedReception === reception.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-slate-100"
                    >
                      <div className="p-4 bg-slate-50 space-y-2">
                        {reception.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-slate-700">{item.item_name}</span>
                            <span className="font-medium text-slate-900">
                              {item.quantity} {item.unit}
                            </span>
                          </div>
                        ))}
                        {reception.warehouse_name && (
                          <div className="pt-2 mt-2 border-t border-slate-200 text-xs text-slate-500">
                            Bodega: {reception.warehouse_name}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Barra flotante de resumen - Solo visible cuando hay items */}
      <AnimatePresence>
        {totalItems > 0 && activeTab === 'new' && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-primary shadow-2xl p-4 z-50"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm text-slate-500">Productos seleccionados</p>
                <p className="text-xl font-bold text-slate-900">{totalItems} productos • {totalQuantity} unidades</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCartOpen(true)}
              >
                Ver detalle
              </Button>
            </div>
            
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              size="lg"
              className="w-full h-14 text-lg font-bold"
              data-testid="submit-reception"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Registrando...
                </>
              ) : (
                <>
                  <Check className="w-6 h-6 mr-2" />
                  Guardar Recepción
                </>
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dialog para datos de factura - Se muestra al seleccionar proveedor */}
      <Dialog open={showInvoiceDialog} onOpenChange={(open) => {
        if (!open && !invoiceConfirmed) {
          // Si cierra sin confirmar, cancelar la selección
          cancelInvoice();
        } else {
          setShowInvoiceDialog(open);
        }
      }}>
        <DialogContent className="mx-4 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {invoiceConfirmed ? 'Editar Factura' : 'Datos de la Factura'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Proveedor seleccionado */}
            {selectedSupplier && (
              <div className="bg-slate-100 rounded-lg p-3">
                <p className="text-xs text-slate-500">Proveedor</p>
                <p className="font-semibold text-slate-900">{selectedSupplier}</p>
              </div>
            )}

            <div>
              <Label className="text-sm mb-2 block">Número de Factura *</Label>
              <Input
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Ej: FAC-001234"
                className="h-12 text-base"
                data-testid="invoice-input"
                autoFocus
              />
            </div>
            
            <div>
              <Label className="text-sm mb-2 block">Bodega Destino</Label>
              <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                <SelectTrigger className="h-12">
                  <Warehouse className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Selecciona bodega" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map(warehouse => (
                    <SelectItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm mb-2 block">Notas (opcional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observaciones..."
                className="h-12"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={cancelInvoice}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmInvoice}
              disabled={!invoiceNumber.trim() || !selectedWarehouse}
              className="flex-1"
            >
              <Check className="w-4 h-4 mr-2" />
              {invoiceConfirmed ? 'Actualizar' : 'Continuar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sheet para ver el carrito */}
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent side="bottom" className="h-[80vh] rounded-t-3xl">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Productos a Recibir ({totalItems})
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setReceptionItems({})}
                className="text-red-500 hover:text-red-700"
              >
                Limpiar
              </Button>
            </SheetTitle>
          </SheetHeader>
          
          <div className="overflow-y-auto h-[calc(100%-140px)] py-4 space-y-3">
            {cartItems.map(item => (
              <div 
                key={item.id}
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{item.name}</p>
                  <p className="text-xs text-slate-500">{item.code}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => adjustQuantity(item.id, -1)}
                  >
                    <Minus className="w-3 h-3" />
                  </Button>
                  <span className="w-12 text-center font-bold">{item.quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => adjustQuantity(item.id, 1)}
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-500"
                    onClick={() => updateQuantity(item.id, 0)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t">
            <Button
              onClick={() => {
                setCartOpen(false);
                setShowInvoiceDialog(true);
              }}
              className="w-full h-12"
              disabled={cartItems.length === 0}
            >
              <Check className="w-5 h-5 mr-2" />
              Continuar ({totalQuantity} unidades)
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default BodegaReceptionPage;
